/**
 * The typed page or type page the route has open, for the shell around it:
 * the tree highlights its row and the header shows its path. The route names
 * a typed page by issue key or item id; it resolves through the project's
 * tracker room, so nothing is highlighted until that room has the item. The
 * tracker store exists only once the room is open, so the lookup runs in a
 * probe the shell mounts then, which reports back.
 */
import { useEffect, useMemo } from 'react';
import { useCollabPagesState, useSharedFolders } from '@nimbalyst/collab-bundle/docs-ui';
import { useTrackerDataSelector } from '@nimbalyst/collab-bundle/trackers-ui';
import { resolveTrackerItemRef } from '../links/consoleLinkLanding';
import { usePageModule } from './pageModule';
import { usePagesTrackers } from './PagesTrackers';
import { typedPageHeaderPath, typePageHeaderPath, type PagesHeaderPath } from './pagesHeaderPath';

export interface PagesActivePage {
  itemId: string | null;
  typeId: string | null;
  /** Null until the page module and the item or type are known. */
  path: PagesHeaderPath | null;
}

export const NO_ACTIVE_PAGE: PagesActivePage = { itemId: null, typeId: null, path: null };

type ActivePageRoute = { itemRef?: string | null; typeId?: string | null };

/** Mount only inside an open tracker room (`usePagesTrackers().available`). */
export function PagesActivePageProbe({ route, onChange }: { route: ActivePageRoute; onChange: (page: PagesActivePage) => void }): null {
  const page = usePagesActivePage(route);
  useEffect(() => { onChange(page); }, [onChange, page]);
  useEffect(() => () => onChange(NO_ACTIVE_PAGE), [onChange]);
  return null;
}

function usePagesActivePage(route: ActivePageRoute): PagesActivePage {
  const recordsById = useTrackerDataSelector((state) => state.recordsById);
  const { resolver } = usePagesTrackers();
  const module = usePageModule();
  const { documents, typePlacements, itemPlacements } = useCollabPagesState();
  const folders = useSharedFolders();
  const itemId = route.itemRef ? resolveTrackerItemRef(route.itemRef, recordsById) : null;
  const typeId = route.itemRef ? null : route.typeId ?? null;

  const path = useMemo<PagesHeaderPath | null>(() => {
    if (!module) return null;
    const item = module.crumbItemLookup(recordsById);
    if (itemId) {
      const record = recordsById.get(itemId);
      if (!record) return null;
      const crumb = module.trackerPageCrumb(itemId, record.primaryType, { itemPlacements, typePlacements, documents, folders, item });
      const type = { typeId: record.primaryType, name: resolver?.typeName(record.primaryType) ?? record.primaryType };
      return typedPageHeaderPath(crumb, type, typeof record.fields.title === 'string' ? record.fields.title : '');
    }
    if (typeId) {
      const name = resolver?.typeName(typeId);
      if (!name) return null;
      return typePageHeaderPath(module.trackerPageCrumbFolders(typeId, typePlacements, folders, { itemPlacements, item }), name);
    }
    return null;
  }, [module, recordsById, itemId, typeId, resolver, itemPlacements, typePlacements, documents, folders]);

  return useMemo(() => ({ itemId, typeId, path }), [itemId, typeId, path]);
}
